import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { environment } from '@/environment';
import { logger } from '@/utils';
import { cookiesUtils } from '@/utils';
import {
  GOOGLE_AUTH_COOKIE_EXPIRES_IN_DAYS,
  GOOGLE_AUTH_SCOPE,
} from '@/constants/features/web-history-auth';
import { apiWebHistory } from '@/utils/api/apiWebHistory';
import {
  getWebHistoryFromStorage,
  removeWebHistoryFromStorage,
  saveWebHistoryToStorage,
} from '@/utils/storage/webHistoryStorage';
import { COOKIE_KEYS } from '@/constants/storage/cookies';
import { ROUTES } from '@/constants/app/routes';
import { RootState } from '@/store/store';
import {
  clearWebHistory,
  setWebHistory,
  setWebHistoryError,
  setWebHistoryLoading,
} from '@/store/reducers';
import { LOGGER_FEATURE, LOGGER_ACTION } from '@/constants/logger/logger-tags';

const POST_AUTH_NAV_ROUTE_KEY = 'postGoogleAuthNavigationRoute';

const AUTH_STATE_KEY = 'googleAuthState';
const AUTH_STATE_BYTES = 16;

const generateAuthState = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(AUTH_STATE_BYTES));
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
};

const isGoogleCodeClientAvailable = (): boolean =>
  typeof google !== 'undefined' &&
  typeof google?.accounts?.oauth2?.initCodeClient === 'function';

const getCallbackUrl = (): string =>
  `${window.location.origin}${environment.baseUrl}${ROUTES.WEB_HISTORY_CALLBACK}`;

const logWebHistoryError = (
  message: string,
  error: unknown,
  action: string
): void => {
  logger.error(message, error, {
    tags: { feature: LOGGER_FEATURE.WEB_HISTORY, action },
  });
};

export const useWebHistoryAuth = () => {
  const webHistoryState = useSelector((state: RootState) => state.webHistory);
  const dispatch = useDispatch();
  const isLoadingRef = useRef(false);

  const setPostSuccessNavigationUrl = (route: string) => {
    sessionStorage.setItem(POST_AUTH_NAV_ROUTE_KEY, route);
  };
  const getPostSuccessNavigationRoute = () => {
    const route = sessionStorage.getItem(POST_AUTH_NAV_ROUTE_KEY);
    sessionStorage.removeItem(POST_AUTH_NAV_ROUTE_KEY);
    return route;
  };

  const reportAuthFailure = useCallback(
    (message: string, error?: unknown): void => {
      logWebHistoryError(message, error, LOGGER_ACTION.GOOGLE_AUTH);
      dispatch(setWebHistoryError());
    },
    [dispatch]
  );

  const verifyAuthState = (returnedState: string | null): boolean => {
    const expectedState = sessionStorage.getItem(AUTH_STATE_KEY);
    sessionStorage.removeItem(AUTH_STATE_KEY);
    const isValid = !!expectedState && expectedState === returnedState;
    if (!isValid) reportAuthFailure('Web history auth state mismatch');
    return isValid;
  };

  const handleGoogleResponse = useCallback(
    async (responseCode: string, redirectUri?: boolean): Promise<boolean> => {
      const redirectUrlAddress = redirectUri ? getCallbackUrl() : '';
      dispatch(setWebHistoryLoading(true));
      try {
        const result = await apiWebHistory.postGoogleAuth(
          responseCode,
          redirectUri,
          redirectUrlAddress
        );
        if (typeof result?.uuid !== 'string' || !result.uuid) {
          throw new Error('Web history auth response has no uuid');
        }
        const suggestions = result.suggestions ?? [];
        cookiesUtils.setCookie(COOKIE_KEYS.GOOGLE_AUTH_UUID, result.uuid, {
          expires: GOOGLE_AUTH_COOKIE_EXPIRES_IN_DAYS,
        });
        saveWebHistoryToStorage(suggestions);
        dispatch(setWebHistory(suggestions));
        return true;
      } catch (error) {
        reportAuthFailure('Failed to handle Google auth response', error);
        return false;
      } finally {
        dispatch(setWebHistoryLoading(false));
      }
    },
    [dispatch, reportAuthFailure]
  );

  const onCtaClick = (
    postAuthRedirectRoute = '',
    permissions = [GOOGLE_AUTH_SCOPE.HISTORY],
    onAuthResult?: (isGranted: boolean) => void
  ) => {
    if (!isGoogleCodeClientAvailable()) {
      reportAuthFailure('Google Identity Services is not loaded');
      onAuthResult?.(false);
      return;
    }
    const authConfig: google.accounts.oauth2.CodeClientConfig = {
      client_id: environment.googleAuthClientId,
      callback: (codeResponse: google.accounts.oauth2.CodeResponse) => {
        if (codeResponse?.error || !codeResponse?.code) {
          logger.debug(`error while trying to get web history`, {
            error: codeResponse?.error,
          });
          onAuthResult?.(false);
          return;
        }
        void handleGoogleResponse(
          codeResponse.code,
          !!postAuthRedirectRoute
        ).then(isGranted => onAuthResult?.(isGranted));
      },
      error_callback: (error: google.accounts.oauth2.ClientConfigError) => {
        logWebHistoryError(
          'Google auth error callback triggered',
          error,
          LOGGER_ACTION.GOOGLE_AUTH
        );
        onAuthResult?.(false);
      },
      scope: permissions.join(' '),
    };
    if (postAuthRedirectRoute) {
      const authState = generateAuthState();
      sessionStorage.setItem(AUTH_STATE_KEY, authState);
      setPostSuccessNavigationUrl(postAuthRedirectRoute);
      authConfig.state = authState;
      authConfig.redirect_uri = getCallbackUrl();
      authConfig.ux_mode = 'redirect';
    }
    try {
      google.accounts.oauth2.initCodeClient(authConfig).requestCode();
    } catch (error) {
      reportAuthFailure('Failed to start Google auth flow', error);
      onAuthResult?.(false);
    }
  };

  const hasWebHistoryPermission = (): boolean => {
    return !!cookiesUtils.getCookie(COOKIE_KEYS.GOOGLE_AUTH_UUID);
  };
  const getWebHistoryUid = (): string | null => {
    return cookiesUtils.getCookie(COOKIE_KEYS.GOOGLE_AUTH_UUID);
  };

  const loadWebHistory = useCallback(async (): Promise<void> => {
    const uid = cookiesUtils.getCookie(COOKIE_KEYS.GOOGLE_AUTH_UUID);
    if (!uid || isLoadingRef.current) return;

    isLoadingRef.current = true;
    dispatch(setWebHistoryLoading(true));
    try {
      const dataFromRemote = await apiWebHistory.getUserWebHistory(uid);
      const suggestions = dataFromRemote ?? [];
      saveWebHistoryToStorage(suggestions);
      dispatch(setWebHistory(suggestions));
    } catch (error) {
      logWebHistoryError(
        'Failed to load web history from API',
        error,
        LOGGER_ACTION.LOAD
      );
      const dataFromStorage = getWebHistoryFromStorage();
      if (dataFromStorage) {
        dispatch(setWebHistory(dataFromStorage));
      } else {
        dispatch(setWebHistoryError());
      }
    } finally {
      isLoadingRef.current = false;
      dispatch(setWebHistoryLoading(false));
    }
  }, [dispatch]);

  const signOutWebHistory = useCallback((): void => {
    removeWebHistoryFromStorage();
    cookiesUtils.removeCookie(COOKIE_KEYS.GOOGLE_AUTH_UUID);
    dispatch(clearWebHistory());
  }, [dispatch]);

  return {
    onCtaClick: onCtaClick,
    webHistoryItems: webHistoryState.webHistoryItems,
    isWebHistoryLoading: webHistoryState.loading,
    hasWebHistoryLoaded: webHistoryState.hasLoaded,
    hasWebHistoryError: webHistoryState.hasError,
    handleGoogleResponse: handleGoogleResponse,
    verifyAuthState: verifyAuthState,
    getPostSuccessNavigationRoute: getPostSuccessNavigationRoute,
    hasWebHistoryPermission: hasWebHistoryPermission,
    getWebHistoryUid: getWebHistoryUid,
    loadWebHistory: loadWebHistory,
    signOutWebHistory: signOutWebHistory,
  };
};
